// src/lib/marketingEvents.ts
import { supabase } from '../services/supabaseClient';

type MarketingEventInput = {
  event_type?: string;
  event_name?: string;
  label?: string;
  target_url?: string;
  source?: string;
  path?: string;
  metadata?: Record<string, unknown>;
};

const SESSION_KEY = 'forfeo_marketing_session_id';
const DISABLE_KEY = 'forfeo_disable_marketing_tracking';

function createId() {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch (_) {}
  return `mf_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function getMarketingSessionId() {
  if (typeof window === 'undefined') return createId();
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = createId();
    window.localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch (_) {
    return createId();
  }
}

function readUtmParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'].forEach((key) => {
    const value = params.get(key);
    if (value) out[key] = value;
  });
  return out;
}

export async function trackMarketingEvent(input: MarketingEventInput = {}) {
  if (typeof window === 'undefined') return { ok: false, skipped: true };
  try {
    if (window.localStorage.getItem(DISABLE_KEY) === 'true') return { ok: false, skipped: true };
  } catch (_) {}

  const payload = {
    event_type: input.event_type || 'custom',
    event_name: input.event_name || input.label || input.event_type || 'custom_event',
    label: input.label || '',
    target_url: input.target_url || '',
    source: input.source || 'frontend',
    path: input.path || `${window.location.pathname}${window.location.search}`,
    page_title: document.title || '',
    referrer: document.referrer || '',
    session_id: getMarketingSessionId(),
    user_agent: navigator.userAgent || '',
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    metadata: {
      ...readUtmParams(),
      language: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...input.metadata,
    },
  };

  try {
    const { data, error } = await supabase.functions.invoke('track-marketing-event', { body: payload });
    if (error) return { ok: false, error };
    return data;
  } catch (error) {
    console.warn('Forfeo marketing tracking skipped:', error);
    return { ok: false, error };
  }
}

export function trackOutboundClick(targetUrl: string, label = 'outbound_click', metadata: Record<string, unknown> = {}) {
  return trackMarketingEvent({
    event_type: 'outbound_click',
    event_name: label,
    label,
    target_url: targetUrl,
    source: 'frontend_click',
    metadata,
  });
}
